import { stringify } from 'query-string';

import { AxiosAdapter } from './adapters/axios-adapter';
import type { IAdapter, IConfig, IResponse, IHttpClient, TParams, TOptions } from './models';
import { checkIsFormData, getFormatHeaders, getFormatOptions } from './helpers';

export class HttpClient implements IHttpClient {
    private adapter: IAdapter;

    private headers: Record<string, string>;

    constructor(Adapter: { create: (config: IConfig) => IAdapter }, config: IConfig = {}) {
        this.headers = getFormatHeaders(config.headers);
        this.adapter = Adapter.create(config);
    }

    static create(config: IConfig) {
        return new HttpClient(AxiosAdapter, config);
    }

    create(config: IConfig = {}) {
        return this.adapter.create(config);
    }

    addHeader(key: string, value: string) {
        this.headers[key] = value;
    }

    removeHeader(key: string) {
        if (this.headers) {
            delete this.headers[key];
        }
    }

    head<T>(
        endpoint: string,
        params?: Record<string, unknown> | null,
        options?: TOptions
    ): Promise<IResponse<T>> {
        return this.send(endpoint, { ...options, params, method: 'HEAD' });
    }

    get<T>(
        endpoint: string,
        params?: Record<string, unknown> | null,
        options?: TOptions
    ): Promise<IResponse<T>> {
        const [url, queryString] = endpoint.split('?');
        const urlParams = new URLSearchParams(queryString);

        return this.send(url, {
            ...options,
            params: { ...params, ...Object.fromEntries(urlParams.entries()) },
            method: 'GET',
        });
    }

    post<T>(endpoint: string, body?: TParams, options?: TOptions): Promise<IResponse<T>> {
        return this.send(endpoint, { ...options, body, method: 'POST' });
    }

    put<T>(endpoint: string, body?: TParams, options?: TOptions): Promise<IResponse<T>> {
        return this.send(endpoint, { ...options, body, method: 'PUT' });
    }

    patch<T>(endpoint: string, body?: TParams, options?: TOptions): Promise<IResponse<T>> {
        return this.send(endpoint, { ...options, body, method: 'PATCH' });
    }

    delete<T>(
        endpoint: string,
        params?: Record<string, unknown> | null,
        options?: TOptions
    ): Promise<IResponse<T>> {
        return this.send(endpoint, { ...options, params, method: 'DELETE' });
    }

    private getParams(data?: Record<string, unknown> | null) {
        return data ? stringify(data) : '';
    }

    private send<T>(
        endpoint: string,
        {
            body,
            method,
            params,
            ...options
        }: {
            body?: TParams;
            params?: Record<string, unknown> | null;
        } & Omit<RequestInit, 'body'>
    ): Promise<IResponse<T>> {
        let queryParams = this.getParams(params);
        queryParams = queryParams ? `?${queryParams}` : '';

        const isFormData = checkIsFormData(body);

        return this.adapter.send(`${endpoint}${queryParams}`, {
            ...getFormatOptions({
                isFormData,
                options: {
                    ...options,
                    headers: {
                        ...this.headers,
                        ...(options.headers || {}),
                    },
                },
                body,
            }),
            method,
        });
    }
}

export const httpClient = new HttpClient(AxiosAdapter);
